import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { StorageObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { slackHook } from "src/utils/snug-slack-hooks";
import { useMounted } from "../use-mounted";

// Action Types
type StorageStoreAction =
  | { type: "SET_STORAGE"; payload: StorageObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_STORAGE"; payload: StorageObj }
  | { type: "DELETE_STORAGE"; payload: string }
  | { type: "SET_STORAGE_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_STORAGE_SAVING_NEW"; payload: boolean };

// Reducer Function
const storageStoreReducer = (
  state: StorageStoreState,
  action: StorageStoreAction
): StorageStoreState => {
  switch (action.type) {
    case "SET_STORAGE":
      return {
        ...state,
        storage: action.payload,
        storageLoading: false,
      };

    case "SET_LOADING":
      return { ...state, storageLoading: action.payload };

    case "ADD_OR_UPDATE_STORAGE":
      const index = state.storage.findIndex((l) => l.id === action.payload.id);
      let updatedStorage = [...state.storage];
      if (index > -1) {
        updatedStorage[index] = action.payload;
      } else {
        updatedStorage.push(action.payload);
      }
      return { ...state, storage: updatedStorage };

    case "DELETE_STORAGE":
      return {
        ...state,
        storage: state.storage.filter((l) => l.id !== action.payload),
      };
    case "SET_STORAGE_FILES_TO_UPLOAD":
      return { ...state, storageFilestoUpload: action.payload };
    case "SET_STORAGE_SAVING_NEW":
      return { ...state, storageSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface StorageStoreState {
  storage: StorageObj[];
  storageLoading: boolean;
  storageSavingNew: boolean;
  storageFilestoUpload: File[];
}

// Hook
export const useStorageStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(storageStoreReducer, {
    storage: [],
    storageLoading: true,
    storageFilestoUpload: [],
    storageSavingNew: false,
  });

  const deleteStorageById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchStorageData = useV3GenericGetData<StorageObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createStorage = useV3Create<StorageObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateStorage = useV3Update<StorageObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<StorageObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleStorageFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.storageFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "storages",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_STORAGE",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_STORAGE_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            slackHook(
              `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path}`
            );
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          slackHook(
            `Document Error: Failed to upload document for entity_id: ${entityId} and document_id: ${currentFile.path} with message "${error}"`
          );
        }
      }
    },
    [state.storageFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadStorageDocument = useCallback(
    async (storage_id: string, document_id: string | null) => {
      if (!document_id || !storage_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "storages",
        entity_id: storage_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in storage objects' documents array
            downloadFileFromS3(
              res,
              state.storage
                ?.find((p) => p.id === storage_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            slackHook(
              `Document Error: Failed to get document with message "No response"`
            );
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          slackHook(
            `Document Error: Failed to get document with message "${err}"`
          );
        });
    },
    [downloadDocument, state.storage, ud_id]
  );

  const handleDeleteStorageDocument = useCallback(
    async (storage_id: string, document_id: string | null): Promise<void> => {
      if (!document_id || !storage_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "storages",
          entity_id: storage_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the storage object
          const updatedStorage = state.storage.map((p) => {
            if (p.id === storage_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_STORAGE",
            payload: updatedStorage,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.storage, deleteDocumentById, ud_id]
  );

  const handleStorageFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_STORAGE_FILES_TO_UPLOAD",
        payload: [...state.storageFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.storageFilestoUpload]
  );

  const handleStorageFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.storageFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_STORAGE_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.storageFilestoUpload]
  );

  const handleStorageFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_STORAGE_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchStorageItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchStorageData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "storages",
        });

        if (isMounted()) {
          dispatch({ type: "SET_STORAGE", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchStorageData, isMounted]
  );

  const handleAddOrUpdateStorageSingleCall = useCallback(
    async (storageItem: StorageObj): Promise<StorageObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(storageItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateStorage(
            {
              ud_id: ud_id,
              entity: "storages",
              entity_id: storageItem.id || undefined,
            },
            storageItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createStorage(
            { ud_id: ud_id, entity: "storages" }, // Adjust according to your actual API parameter requirements
            storageItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_STORAGE", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save storage item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createStorage, updateStorage]
  );

  const handleAddOrUpdateStorage = useCallback(
    async (storageItem: StorageObj) => {
      dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: true });
      if (state.storageFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateStorageSingleCall(storageItem);
          if (res) {
            await handleStorageFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error("Failed to add or edit storage data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateStorageSingleCall(storageItem);
        } catch (error) {
          toast.error("Failed to add or edit storage data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateStorageSingleCall,
      state.storageFilestoUpload,
      handleStorageFilesUpload,
    ]
  );

  const handleDeleteStorage = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: true });
      try {
        if (!ud_id || !id) {
          return Promise.reject("Missing required parameters");
        }
        
        await deleteStorageById({
          ud_id: ud_id,
          entity: "storages",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_STORAGE", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_STORAGE_SAVING_NEW", payload: false });
      }
    },
    [deleteStorageById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchStorageItems(ud_id); // Fetch all storage items
    }
  }, [ fetchStorageItems, ud_id]);

  return {
    storage: state.storage,
    storageLoading: state.storageLoading,
    storageSavingNew: state.storageSavingNew,
    storageFilestoUpload: state.storageFilestoUpload,
    handleAddOrUpdateStorage,
    handleDeleteStorage,
    handleStorageFileRemove,
    handleStorageFilesDrop,
    handleStorageFilesRemoveAll,
    handleDownloadStorageDocument,
    handleDeleteStorageDocument,
    handleStorageFilesUpload,
  };
};
